<template>
  <div>

    <modal-component v-model="modal.createSupplierAccount.active" size="small">
      <div slot="title">{{ modal.createSupplierAccount.title }}</div>
      <create-supplier-account
        :supplierID="editSupplierID"
        :accountID="editSupplierAccountID"
        :create-alert="false"
        @created="closeCreateSupplierAccountModal($event)"
        @updated="closeCreateSupplierAccountModal($event)"/>
    </modal-component>

    <div class="flex items-center -mx-2" v-if="selectable">
      <div class="px-2 text-4xl">
        <i class="far fa-check-circle text-green-500" v-if="supplierAccountIDSelected"></i>
        <i class="fas fa-arrow-down text-link" v-else></i>
      </div>
      <div class="px-2 leading-tight">
        <p class="text-xl font-bold">SELECT SUPPLIER ACCOUNT</p>
        <p>click on the account to select it</p>
      </div>
    </div>

    <div class="box">
      <div class="flex flex-wrap items-center">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getSupplierAccounts" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <div
            v-if="supplierID && hasPermission('supplier-account.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="createSupplierAccount"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New account</span>
          </div>

          <div
             v-if="!supplierID && hasPermission('supplier.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="$router.push('/supplier/create/accounts')"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New Suplier</span>
          </div>

          <button
            v-if="hasPermission('vehicle.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.supplierAccount.enabled"
            @click="exportSupplierAccounts"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.supplierAccount.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getSupplierAccounts">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <supplier-account-filters
            :filters.sync="filters"
            @updated="getSupplierAccounts"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>

        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">
          <p class="text-lg text-center my-12" v-if="!hasSupplierAccounts">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Unpaid total</th>
                  <th v-if="!supplierID">Supplier</th>
                  <th>Created at</th>
                  <th v-if="hasAnyOfPermissions(['supplier-account.edit', 'supplier-account.delete'])">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(sa, i) in supplierAccounts" :key="i" @click="selectSupplierAccount(sa)" :class="{'selected': supplierAccountIDSelected === sa.id}">
                  <td>{{ sa.id }}</td>
                  <td>{{ sa.name }}</td>
                  <td>{{ sa.code }}</td>
                  <td>
                    <span class="text-red-500" v-if="sa.unpaid_total">{{ $formatMoney(sa.unpaid_total) }}</span>
                  </td>
                  <td v-if="!supplierID">
                    <a class="text-link" @click.stop="$router.push('/supplier/details/' + sa.contact.id)">
                      {{ sa.contact.first_name + ' ' + sa.contact.last_name + (sa.contact.company_name ? ' (' + sa.contact.company_name + ')' : '') }}
                    </a>
                  </td>
                  <td>{{ $moment(sa.created_at).format(config.dateTimeFormats.display) }}</td>
                  <td v-if="hasAnyOfPermissions(['supplier-account.edit', 'supplier-account.delete'])">
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="editSupplierAccount(sa.contact.id || supplierID, sa.id)"
                        v-if="hasPermission('supplier-account.edit')"
                      ><i class="fas fa-edit"></i></a>
                      <a
                      class="hover:text-theme-darker text-base cursor-pointer p-2"
                      @click.stop="deleteSupplierAccount(sa.id)"
                      v-if="hasPermission('supplier-account.delete')"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasSupplierAccounts && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const SupplierAccountFilters = resolve => require(['@/components/filters/Filters'], resolve)
const ModalComponent = resolve => require(['@/components/Modal'], resolve)
const CreateSupplierAccount = resolve => require(['@/components/pages/suppliers/AccountEdit'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  name: 'supplier-account-list',
  props: {
    supplierID: {},
    selectable: Boolean,
    selectedAccountID: {},
    inModal: Boolean,
  },
  components: {
    FilteringToggleButton,
    SupplierAccountFilters,
    ListSearch,
    ModalComponent,
    CreateSupplierAccount,
    PaginationComponent,
  },
  data() {
    return {
      listAll: this.$route.name === 'supplierAccount.list',
      supplierAccounts: [],
      searchInput: '',
      editSupplierID: null,
      editSupplierAccountID: null,
      supplierAccountIDSelected: null,
      query: {
        search: '',
      },
      exportFile: {
        supplierAccount: {
          enabled: true,
        }
      },
      filtering: {
        active: false,
        hasChanges: false,
      },
      supplierList: [],
      filters: {
        supplier: {
          key: 'supplier',
          name: 'Supplier',
          active: !this.supplierID,
          modelSelect: {
            propData: {
              model: 'supplier',
              formName: 'filter-supplier',
              list: [],
              searchOnMount: true,
              isTaggable: false,
              noWrap: true,
            },
            listeners: {
              input: (value) => {
                this.filters.supplier.value = value
              },
              'update:list': (list) => this.filters.supplier.modelSelect.propData.list = list,
            }
          },
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      modal: {
        createSupplierAccount: {
          active: false,
          title: '',
        },
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      },
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getSupplierAccounts()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getSupplierAccounts()
      }
    },
    supplierID() {
      this.getSupplierAccounts()
    },
    selectedAccountID: {
      handler(id) {
        if (id) {
          this.supplierAccountIDSelected = id
        } else {
          this.supplierAccountIDSelected = null
        }
      },
      immediate: true
    },
  },
  computed: {
    hasSupplierAccounts() {
      return Object.entries(this.supplierAccounts).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
      hasAnyOfPermissions: 'user/hasAnyOfPermissions',
    })
  },
  mounted() {
    this.getSupplierAccounts()
  },
  methods: {
    selectSupplierAccount(account) {
      if (!this.selectable) {
        return
      }
      if (account && account.id) {
        if (account.id === this.supplierAccountIDSelected) {
          account = {}
        }
        this.supplierAccountIDSelected = account.id
      }

      this.$EventBus.$emit('supplierAccount.selected', account)
    },
    editSupplierAccount(supplierID, accountID) {
      this.editSupplierID = supplierID
      this.editSupplierAccountID = accountID
      // show modal
      this.modal.createSupplierAccount.title = 'Edit supplier account'
      this.modal.createSupplierAccount.active = true
    },
    closeCreateSupplierAccountModal(account, mode) {
      // show alert
      if (mode === 'edit') {
        this.$swal.fire({
          title: 'Done',
          text: 'Supplier account edited successfully!',
          type: 'success',
        })
      } else if (mode === 'create') {
        this.$swal.fire({
          title: 'Done',
          text: 'Supplier account created successfully!',
          type: 'success',
        })
      }

      // close modal
      this.modal.createSupplierAccount.active = false

      // select the newly created account?
      if (this.selectable) {
        this.supplierAccountIDSelected = account.id
        this.$EventBus.$emit('supplierAccount.selected', account)
      }

      // get supplier accounts again
      this.getSupplierAccounts()
    },
    createSupplierAccount(){
      this.editSupplierID = this.supplierID
      this.editSupplierAccountID = null

      // show modal
      this.modal.createSupplierAccount.title = 'Create supplier account'
      this.modal.createSupplierAccount.active = true
    },
    exportSupplierAccounts() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      if (this.filters.supplier.value) {
        filters.supplier_id = this.filters.supplier.value.id
      }
      if (this.supplierID) {
        filters.supplier_id = this.supplierID
      }

      this.exportFile.supplierAccount.enabled = false
      axios.get('supplier-accounts/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.supplierAccount.enabled = true
        })
    },
    deleteSupplierAccount(supplierAccountID) {
      this.$swal.fire({
        title: 'Delete supplier account',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            axios.delete('supplier-accounts/' + supplierAccountID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getSupplierAccounts()
              })
          }
        })
    },
    getSupplierAccounts() {
      if (!this.supplierID && !this.listAll) {
        this.supplierAccounts = []
        return false
      }

      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      if (this.filters.supplier.value) {
        filters.supplier_id = this.filters.supplier.value.id
      }
      if (this.supplierID) {
        filters.supplier_id = this.supplierID
      }

      axios.get('supplier-accounts', {params:{
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          const res = response.data
          const supplierAccounts = res.data
          this.pagination.perPage = res.per_page
          this.pagination.totalRows = res.total

          if (supplierAccounts.length) {
            axios.post('reports/supplier-accounts/unpaid-total', {
              accounts: supplierAccounts.map(s => s.id)
            })
              .then(response => {
                const totals = response.data
                for (let i in totals) {
                  for (let j in supplierAccounts) {
                    if (totals[i].id === supplierAccounts[j].id) {
                      this.$set(supplierAccounts[j], 'unpaid_total', totals[i].unpaid_total)
                    }
                  }
                }
                // We can set this.supplierAccounts here and it will prevent the delayed display
                // of unpaid_total but it will also delay the whole table.
              })
          }

          this.supplierAccounts = Object.assign([], supplierAccounts)

          this.query.search = this.searchInput
        })
    }
  }
}
</script>
